import axios from 'axios';
import { API_BASE_URL } from './api';

const DOCUMENTS_URL = `${API_BASE_URL}/documents`;

/**
 * Document API service for managing transaction documents
 */
export const documentApi = {
  /**
   * Upload a single document
   * @param {string} transactionType - Type of transaction (e.g., 'gsec', 'money_market', 'fixed_deposit')
   * @param {string} transactionId - Transaction ID or deal number
   * @param {File} file - File to upload
   * @param {string} description - Optional description
   * @returns {Promise<Object>} Uploaded document data
   */
  uploadDocument: async (transactionType, transactionId, file, description = '') => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('transaction_type', transactionType);
      formData.append('transaction_id', String(transactionId));
      if (description) {
        formData.append('description', description);
      }

      const response = await axios.post(
        `${DOCUMENTS_URL}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 60000, // 60 seconds for file uploads
        }
      );

      return response.data.documents?.[0] || response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  /**
   * Upload multiple documents
   * @param {string} transactionType - Type of transaction
   * @param {string} transactionId - Transaction ID or deal number
   * @param {File[]} files - Array of files to upload
   * @param {string} description - Optional description
   * @returns {Promise<Array>} Array of uploaded document data
   */
  uploadMultiple: async (transactionType, transactionId, files, description = '') => {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('documents', file);
      });
      formData.append('transaction_type', transactionType);
      formData.append('transaction_id', String(transactionId));
      if (description) {
        formData.append('description', description);
      }

      const response = await axios.post(
        `${DOCUMENTS_URL}/upload-multiple`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000, // 120 seconds for multiple file uploads
        }
      );

      return response.data.documents || [];
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  },

  /**
   * Get all documents for a transaction
   * @param {string} transactionType - Type of transaction
   * @param {string} transactionId - Transaction ID or deal number
   * @returns {Promise<Array>} Array of document objects
   */
  getDocuments: async (transactionType, transactionId) => {
    try {
      const response = await axios.get(
        `${DOCUMENTS_URL}/${transactionType}/${transactionId}`
      );
      return response.data.documents || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
  },

  /**
   * Download a document
   * @param {number} documentId - Document ID
   * @returns {Promise<Blob>} File blob
   */
  downloadDocument: async (documentId) => {
    try {
      const response = await axios.get(
        `${DOCUMENTS_URL}/${documentId}/download`,
        {
          responseType: 'blob',
          timeout: 60000,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  /**
   * Delete a document
   * @param {number} documentId - Document ID
   * @returns {Promise<boolean>} Success status
   */
  deleteDocument: async (documentId) => {
    try {
      const response = await axios.delete(`${DOCUMENTS_URL}/${documentId}`);
      return response.data.success || false;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  /**
   * Update document metadata
   * @param {number} documentId - Document ID
   * @param {Object} updates - Fields to update (description, file_name)
   * @returns {Promise<Object>} Updated document data
   */
  updateDocument: async (documentId, updates) => {
    try {
      const response = await axios.put(
        `${DOCUMENTS_URL}/${documentId}`,
        updates
      );
      return response.data.document || null;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  /**
   * Helper to trigger file download in browser
   * @param {number} documentId - Document ID
   * @param {string} fileName - Original file name
   */
  downloadAndSave: async (documentId, fileName) => {
    try {
      const blob = await documentApi.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading and saving file:', error);
      throw error;
    }
  },
};

export default documentApi;
